import { FC, useContext } from 'react';
import { Controller } from 'react-hook-form';
import { Button, Chip, Grid, Stack, TextField, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { DialogContext } from '@cvt/contexts';


export const CharacterDevelopmentFields: FC = () => {
  const { openDialog } = useContext(DialogContext);

  const handleAddCharacter = (onChange: (value: Stories.SupportCharacter[]) => void, currentValue: Stories.SupportCharacter[]) => {
    openDialog('supportCharacter', {
      onSubmit: (character: Stories.SupportCharacter) => {
        onChange([...currentValue, character]);
      },
    });
  };

  const handleEditCharacter = (
    onChange: (value: Stories.SupportCharacter[]) => void, 
    currentValue: Stories.SupportCharacter[], 
    index: number, 
    character: Stories.SupportCharacter,
  ) => {
    openDialog('supportCharacter', {
      character,
      onSubmit: (updatedCharacter: Stories.SupportCharacter) => {
        const newCharacters = [...currentValue];
        newCharacters[index] = updatedCharacter;
        onChange(newCharacters);
      },
    });
  };

  return (
    <Stack gap={3}>
      <Typography variant="body1" fontSize={30} fontWeight={600}>
        Character Development
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Define your story's key characters and their development
      </Typography>

      <Grid container gap={2}>
        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Protagonist</Typography>
          <Controller
            name="characters.protagonist.name"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={1}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder="e.g., Tim Hebert"
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Want</Typography>
          <Typography variant="caption" color="text.secondary">
            What does the protagonist desire to achieve?
          </Typography>
          <Controller
            name="characters.protagonist.want"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={3}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder="e.g., To summit Kilimanjaro - Achievement, Challenge, Mastery, Validation (endurance and Planning)"
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Need</Typography>
          <Typography variant="caption" color="text.secondary">
            What unconscious transformation is required?
          </Typography>
          <Controller
            name="characters.protagonist.need"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={3}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder="e.g., Unconscious Transformation - to let go of the Lone Climber Myth"
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Change</Typography>
          <Typography variant="caption" color="text.secondary">
            What is the moment of transformation?
          </Typography>
          <Controller
            name="characters.protagonist.change"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={2}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder="e.g., Moment of Surrender"
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Antagonist</Typography>
          <Typography variant="caption" color="text.secondary">
            Person, system, belief, or internal opposition
          </Typography>
          <Controller
            name="characters.antagonist.name"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={1}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder="e.g., The Mountain"
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Antagonist Description</Typography>
          <Controller
            name="characters.antagonist.description"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={2}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder="e.g., My pride (not always a person)"
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Supporting Characters</Typography>
          <Controller
            name="characters.supportcharacters"
            render={({ field }) => {
              const supportingCharacters = field.value || [];

              return (
                <Stack>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<Add/>}
                      onClick={() => handleAddCharacter(field.onChange, supportingCharacters)}
                    >
                      Add Supporting Character
                    </Button>
                  </Stack>
                  <Stack gap={2} direction="row" flexWrap="wrap" mt={2}>
                    {supportingCharacters.map((character: Stories.SupportCharacter, index: number) => (
                      <Chip
                        key={index}
                        label={`${character.name} (${character.role})`}
                        variant="outlined"
                        onClick={() => handleEditCharacter(field.onChange, supportingCharacters, index, character)}
                        onDelete={() => {
                          const newCharacters = [...supportingCharacters];
                          newCharacters.splice(index, 1);
                          field.onChange(newCharacters);
                        }}
                        sx={{
                          height: 'auto',
                          fontWeight: 500,
                          '& .MuiChip-label': {
                            whiteSpace: 'wrap',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              );
            }}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
